<template>
  <div class="hosting-period">
    <!-- TITULO -->
    <div class="form--title">
      <PmwHeading size="paragraph-80" weight="weight-light">
        Período de hospedagem
      </PmwHeading>

      <PmwParagraph class="form--subtitle" size="paragraph-40">
        Período que a tarifa estará disponível para hospedagem.
      </PmwParagraph>
    </div>

    <div class="form--row">
      <!-- INPUT DE PERÍODO DE HOSPEDAGEM (RANGE) -->
      <PmwFormDatepicker
        v-model="hostingDate"
        :range="true"
        label="Período de hospedagem"
        placeholder="01/01/2023 - 01/01/2024"
        size="large"
        class="form--input__datepicker"
      />

      <!-- BOTÃO DE ADICIONAR DATA -->
      <PmwButton
        :hasIcon="true"
        :disabled="!hostingDate"
        variation="blue-100"
        class="form--button"
        size="large"
        @click="addDate($event)"
      >
        Adicionar
      </PmwButton>
    </div>

    <!-- CHIP COM PERÍODOS ADICIONADOS -->
    <div
      v-if="
        fluctuationData.periodosHospedagem &&
          fluctuationData.periodosHospedagem.length >= 1
      "
      class="form--row__small"
    >
      <Chip
        v-for="(period, i) in fluctuationData.periodosHospedagem"
        :key="`period-chip-${i}`"
        class="form--input__period"
        cols="2"
        close
        @close="removeDate(i)"
      >
        {{ formatDate(new Date(period.dataInicio)) }} -
        {{ formatDate(new Date(period.dataFim)) }}
      </Chip>
    </div>

    <!-- CHECKBOX DE DEFINIÇÃO DE DATA OBRIGATÓRIA PARA HOSPEDAGEM -->
    <div class="form--row">
      <InputCheckbox
        v-model="isMandatoryDate"
        :checked="isMandatoryDate"
        :inputValue="1"
        :infoIcon="true"
        tooltipMinWidth="375"
        label="Definir data obrigatória para a hospedagem"
        infoText="A tarifa só aparecerá no motor de reservas quando a data preenchida neste campo estiver dentro período de hospedagem."
        name="isMandatoryDate"
        class="form--input__select"
      />
    </div>

    <!-- INPUT DE DATA OBRIGATÓRIA -->
    <div v-if="isMandatoryDate" class="form--row">
      <PmwFormDatepicker
        v-model="fluctuationData.dataPernoiteObrigatorio"
        label="Data obrigatória"
        placeholder="01/01/2023"
        size="large"
        class="form--input__datepicker"
      />
    </div>

    <!-- CHECKBOX DE MINIMO E MÁXIMO DE NOITES -->
    <div class="form--row">
      <InputCheckbox
        v-model="isMinMaxNights"
        :checked="isMinMaxNights"
        :inputValue="1"
        :infoIcon="true"
        tooltipMinWidth="500"
        label="Definir mínimo e máximo de noites"
        infoText="A tarifa só será ativada quando o hóspede está buscando por uma quantidade de noites igual ou maio que o mínimo e menor ou igual que o máximo especificado."
        name="isMinMaxNights"
        class="form--input__select"
      />
    </div>

    <div v-if="isMinMaxNights" class="form--row">
      <!-- INPUT DE MINIMO DE NOITES -->
      <PmwFormInput
        v-model.number="fluctuationData.minimoNoitesDe"
        :error="errors.includes('minimoNoitesDe')"
        data-error="minimoNoitesDe"
        label="Mínimo de noites"
        placeholder="0"
        type="number"
        size="small"
        sufix="noites"
        class="form--input"
        @blur="handleBlur('minimoNoitesDe', 1, 'minimoNoitesAte')"
      />

      <!-- INPUT DE MÁXIMO DE NOITES -->
      <PmwFormInput
        v-model.number="fluctuationData.minimoNoitesAte"
        :error="errors.includes('minimoNoitesAte')"
        data-error="minimoNoitesAte"
        label="Máximo de noites"
        placeholder="0"
        size="small"
        type="number"
        sufix="noites"
        class="form--input"
        @blur="handleBlur('minimoNoitesAte', 'minimoNoitesDe', null)"
      />
    </div>

    <!-- CHECKBOX DE DEFINIÇÃO DO PERÍODO DE DESTAQUE -->
    <div class="form--row">
      <InputCheckbox
        v-model="hasHighlightPeriod"
        :checked="hasHighlightPeriod"
        :inputValue="1"
        label="Definir período com destaque"
        name="hasHighlightPeriod"
        class="form--input__select"
      />
    </div>

    <PmwParagraph size="paragraph-40" class="form--paragraph">
      O período escolhido terá destaque sobre os demais na criação do link
      público. <br />
      Servirá para melhorar suas vendas em períodos do mês de baixa procura ou
      que estejam com um desconto maior.
    </PmwParagraph>

    <!-- INPUT DE PERÍODO DE DESTAQUE -->
    <div v-if="hasHighlightPeriod" class="form--row">
      <PmwFormDatepicker
        v-model="highlightPeriod"
        :minDate="minHighlightDate"
        :vcalendarOptions="{
          'available-dates': availableHighlightDates
        }"
        :error="highlightPeriodError"
        :showErrorFeedbackMessage="showErrorFeedbackMessage"
        feedbackMessage="Selecione até 10 dias"
        label="Selecione a data início e data término"
        placeholder="00/00/0000 - 00/00/0000"
        size="large"
        class="form--input__datepicker"
        range
        @input="setHighlightPeriod"
      />

      <!-- RADIOS EXIBIDOS APENAS SE PERÍODO DE DESTAQUE <= 10 DIAS -->
      <div
        v-if="
          differenceInDays(highlightPeriod?.end, highlightPeriod?.start) <= 10
        "
        class="form--column"
      >
        <!-- DEFINIR COMO PACOTE FECHADO -->
        <div class="form--column">
          <PmwInputRadio
            v-model="fluctuationData.pacoteFechado"
            :inputValue="true"
            :checked="fluctuationData.pacoteFechado"
            label="Definir como pacote fechado"
          />

          <PmwInfoTooltip width="350px" class="form--column__icon">
            <template #content>
              O pacote fechado considera o dia inicial e o final do período
              selecionado como um todo para aplicação no link público.
            </template>
          </PmwInfoTooltip>
        </div>

        <!-- DEFINIR COMO DIA-A-DIA -->
        <div class="form--column">
          <PmwInputRadio
            v-model="fluctuationData.pacoteFechado"
            :inputValue="false"
            :checked="!fluctuationData.pacoteFechado"
            label="Definir como dia-a-dia"
          />

          <PmwInfoTooltip width="280px" class="form--column__icon">
            <template #content>
              O período dia-a-dia irá buscar dentro dos dias a melhor data para
              aplicar no link público.
            </template>
          </PmwInfoTooltip>
        </div>
      </div>
    </div>

    <PmwAlertModal
      v-model="showPeriodModal"
      cancelButtonText="Voltar e ajustar"
      confirmButtonText=""
      cancelButtonColor="blue-100"
      text="O período em destaque precisa ser de no máximo 10 dias. Ajuste o período selecionado e tente novamente."
      @alert-modal-cancel="fixHighlightPeriod"
    />
  </div>
</template>

<script>
import { differenceInDays } from 'date-fns'
import { formatDate } from '@/helpers/date'
import { clampNumber } from '@/helpers/math'

import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import PmwFormDatepicker from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormDatepicker'
import InputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/InputCheckbox'
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'
import PmwFormInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormInput'
import PmwInputRadio from '@solucoes/components-lib-tmp/src/components/elements/PmwInputRadio'
import PmwInfoTooltip from '@solucoes/components-lib-tmp/src/components/patterns/PmwInfoTooltip'
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import Chip from '@/components/_base/patterns/Chip'

export default {
  name: 'HostingPeriod',
  components: {
    PmwHeading,
    PmwParagraph,
    InputCheckbox,
    PmwFormDatepicker,
    PmwButton,
    Chip,
    PmwFormInput,
    PmwInputRadio,
    PmwInfoTooltip,
    PmwAlertModal
  },

  props: {
    fluctuationData: {
      type: Object,
      default: () => ({})
    },

    /** Valor da data obrigatória para a hospedagem */
    mandatoryDate: {
      type: Object,
      default: () => {}
    },

    /** Erros (array de erros que foram identificados pelo back-end) */
    errors: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      hostingDate: null,

      isMandatoryDate: false,

      isMinMaxNights: false,

      hasHighlightPeriod: false,

      highlightPeriod: null,

      minHighlightDate: new Date(),

      availableHighlightDates: null /** Datas disponíveis para seleção do período de destaque*/,

      showPeriodModal: false,

      showErrorFeedbackMessage: false,

      highlightPeriodError: false
    }
  },

  watch: {
    /**
     * Se o período de destaque tiver mais do que 10 dias, mostra a modal de erro
     * @param {object} newVal: período selecionado
     */
    highlightPeriod(newVal) {
      if (newVal !== null) {
        if (differenceInDays(newVal.end, newVal.start) > 10) {
          this.showPeriodModal = true
        } else {
          this.showErrorFeedbackMessage = false
          this.highlightPeriodError = false
        }
      }
    },

    /**
     * Limpa as datas selecionadas no período de destaque ao desmarcar a checkbox
     * @param {boolean} newVal: valor da checkbox
     */
    hasHighlightPeriod(newVal) {
      if (newVal === false) {
        this.highlightPeriod = null
        this.fluctuationData.dataInicioPeriodoDestaque = null
        this.fluctuationData.dataFimPeriodoDestaque = null
        this.fluctuationData.pacoteFechado = false

        this.showErrorFeedbackMessage = false
        this.highlightPeriodError = false
      }
    },

    'fluctuationData.dataInicioPeriodoDestaque': {
      handler(newVal) {
        if (newVal) {
          this.hasHighlightPeriod = true

          this.highlightPeriod = {
            start: new Date(newVal),
            end: new Date(this.fluctuationData.dataFimPeriodoDestaque)
          }
        }
      },
      immediate: true
    },

    'fluctuationData.periodosHospedagem'(newVal) {
      if (newVal?.length <= 0) {
        this.fluctuationData.diasToleranciaCheckin = null
        this.fluctuationData.diasToleranciaCheckout = null
        this.fluctuationData.coeficienteCheckin = null
        this.fluctuationData.coeficienteCheckout = null

        this.minHighlightDate = new Date()

        this.availableHighlightDates = null
      } else {
        /**
         * Se houver períodos de hospedagem, essas são as únicas datas
         * disponíveis para período de destaque
         */
        this.minHighlightDate = null

        this.availableHighlightDates = newVal.map((item) => {
          return {
            start: new Date(item.dataInicio),
            end: new Date(item.dataFim)
          }
        })
      }
    },

    isMandatoryDate(newVal) {
      if (!newVal) {
        this.fluctuationData.dataPernoiteObrigatorio = null
      }
    },

    isMinMaxNights(newVal) {
      if (!newVal) {
        this.fluctuationData.minimoNoitesDe = null
        this.fluctuationData.minimoNoitesAte = null
      }
    }
  },

  created() {
    if (this.fluctuationData.dataPernoiteObrigatorio) {
      this.isMandatoryDate = true
    }

    if (this.fluctuationData.periodosHospedagem) {
      this.orderHostingPeriod()
    }

    if (
      this.fluctuationData.minimoNoitesDe ||
      this.fluctuationData.minimoNoitesAte
    ) {
      this.isMinMaxNights = true
    }
  },

  methods: {
    /** Limpa o campo de período de destaque e mostra a mensagem de erro */
    fixHighlightPeriod() {
      this.highlightPeriod = null
      this.highlightPeriodError = true
      this.showErrorFeedbackMessage = true
    },

    /**
     * Passa as datas do período de destaque para o objeto com os dados
     * @param {object} event: datas selecionadas
     */
    setHighlightPeriod(event) {
      if (event !== null) {
        this.fluctuationData.dataInicioPeriodoDestaque = event.start
        this.fluctuationData.dataFimPeriodoDestaque = event.end
      } else {
        this.fluctuationData.dataInicioPeriodoDestaque = null
        this.fluctuationData.dataFimPeriodoDestaque = null
      }
    },

    /** Adiciona chip com a data */
    addDate() {
      if (!this.fluctuationData.periodosHospedagem)
        this.fluctuationData.periodosHospedagem = []

      /** Ajusta os valores dos dados de data */
      this.fluctuationData.periodosHospedagem.push({
        dataInicio: this.hostingDate.start,
        dataFim: this.hostingDate.end
      })

      this.orderHostingPeriod()

      this.hostingDate = ''
    },

    /** Remove as Datas */
    removeDate(index) {
      this.fluctuationData.periodosHospedagem.splice(index, 1)
    },

    orderHostingPeriod() {
      this.fluctuationData.periodosHospedagem.sort((a, b) => {
        const dateA = new Date(a.dataFim)
        const dateB = new Date(b.dataFim)
        return dateA - dateB
      })
    },

    /**
     * Ajusta o valor de um campo numérico garantindo que esteja dentro dos limites definidos.
     * @param {String} field: Campo a ajustar.
     * @param {Number|String} min: Valor mínimo ou nome do campo mínimo.
     * @param {Number|String|null} max: Valor máximo, nome do campo máximo ou null para sem limite.
     */
    handleBlur(field, min, max) {
      const currentValue = this.fluctuationData[field]

      /** Se o campo atual está vazio/null, não faz nada */
      if (
        currentValue === null ||
        currentValue === '' ||
        currentValue === undefined
      ) {
        return
      }

      const minValue = typeof min === 'string' ? this.fluctuationData[min] : min
      const maxValue =
        typeof max === 'string' ? this.fluctuationData[max] : max ?? null

      /** Se o campo de referência (max ou min) não foi preenchido, apenas garante >= 1 */
      if (typeof max === 'string' && !maxValue) {
        this.fluctuationData[field] = clampNumber(
          currentValue,
          minValue || 1,
          null
        )
        return
      }

      if (typeof min === 'string' && !minValue) {
        this.fluctuationData[field] = clampNumber(currentValue, 1, maxValue)
        return
      }

      this.fluctuationData[field] = clampNumber(
        currentValue,
        minValue || 1,
        maxValue
      )
    },

    differenceInDays,
    formatDate: formatDate
  }
}
</script>

<style lang="scss" scoped>
.form {
  &--subtitle {
    font-size: font-size('size-small');
    font-weight: font-weight('weight-normal');
    color: pmw-colors(grey-80);
    line-height: 21px;
  }

  &--input {
    /** Alterar cores novas do select */
    ::v-deep {
      .pmw-form-field--label {
        font-weight: font-weight('weight-medium');
      }
      .form-field--label-wrapper {
        line-height: 21px;
        margin-bottom: 3px;
      }
      .pmw-input {
        background: $white;
      }
      .pmw-input--clear {
        z-index: 0;
      }
      .pmw-input__focus {
        border-color: $pmw-blue;
      }

      .pmw-input--field {
        background: $pmw-white;

        &:focus {
          border-color: $pmw-blue;
        }
      }

      .pmw-input__disabled {
        input {
          background: pmw-colors('grey-20');
          color: $pmw-gray-darker;
        }
      }

      .pmw-custom-tooltip {
        line-height: 18px;
      }
    }

    &__select {
      width: 435px;
      max-width: 435px;

      &.error {
        ::v-deep {
          .pmw-select--button {
            border-bottom-color: pmw-colors('error-100');
            border-bottom-width: 2px;
          }
        }
      }
    }

    &__datepicker {
      margin-right: 10px;

      ::v-deep {
        .pmw-input--field:focus {
          border-color: pmw-colors(blue-100);
        }

        .pmw-input--clear {
          color: pmw-colors(blue-100);
        }
      }
    }
  }

  &--row {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 24px;
    margin-bottom: 32px;

    &__small {
      max-width: 500px;
      gap: 0px;
      margin-top: -15px;
      margin-bottom: 32px;
    }
  }

  &--title {
    margin-bottom: 24px;
    position: relative;
  }

  &--column {
    display: inline-flex;
    gap: 5px;
    align-items: center;

    &__icon {
      color: $pmw-gray-dark;
    }
  }

  &--paragraph {
    color: pmw-colors('grey-70');
    margin-top: -14px;
    margin-bottom: 24px;
  }
}

::v-deep {
  .pmw-input-radio {
    input {
      &:checked ~ .pmw-input-radio--fake-radio {
        border-color: pmw-colors('blue-100');

        &:before {
          background-color: pmw-colors('blue-100');
        }
      }
    }
  }
}
</style>
